import React, { useEffect, useState } from "react";
import { getData } from "../../../functions/request";
import { DescriptionPeriodValue } from "../../../layout";
import {
  formatDateHourToBR,
  saveData,
} from "../../../functions/functionsUtility";
import { Button, Card, Table, DatePicker, Typography, Tag, Space } from "antd";
import { FaSync } from "react-icons/fa";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

// Calcula a data de trade: -1 dia, ou -2 dias se for segunda/domingo (pega sexta-feira)
function getLastTradeDate() {
  const today = dayjs();
  const dayOfWeek = today.day(); // 0=domingo, 1=segunda, ..., 6=sábado

  if (dayOfWeek === 1) {
    return today.subtract(2, "day");
  } else if (dayOfWeek === 0) {
    return today.subtract(2, "day");
  } else {
    return today.subtract(1, "day");
  }
}

export default function PetroleoNy() {
  const [cotacoesData, setCotacoesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // CME Settlements State
  const [cmeData, setCmeData] = useState([]);
  const [cmeLoading, setCmeLoading] = useState(false);
  const [cmeTradeDate, setCmeTradeDate] = useState(getLastTradeDate());
  const [cmeHeader, setCmeHeader] = useState("");
  const [cmeUpdateTime, setCmeUpdateTime] = useState("");
  const [cmeReportType, setCmeReportType] = useState("");

  const fetchCmeSettlements = async (date) => {
    setCmeLoading(true);
    try {
      const formattedDate = date.format("MM/DD/YYYY");
      const timestamp = Date.now();
      const url = `https://www.cmegroup.com/CmeWS/mvc/Settlements/Futures/Settlements/425/FUT?strategy=DEFAULT&tradeDate=${formattedDate}&pageSize=500&isProtected&_t=${timestamp}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data && data.settlements) {
        const filteredData = data.settlements
          .filter((item) => item.month !== "Total")
          .map((item, index) => ({
            key: index,
            ...item,
          }));
        setCmeData(filteredData);
        setCmeHeader(data.dsHeader || "");
        setCmeUpdateTime(data.updateTime || "");
        setCmeReportType(data.reportType || "");
      } else {
        setCmeData([]);
        toast.warning("Nenhum dado encontrado para a data selecionada.");
      }
    } catch (error) {
      console.error("Erro ao buscar cotações CME:", error);
      toast.error("Erro ao buscar cotações do CME Group.");
      setCmeData([]);
    } finally {
      setCmeLoading(false);
    }
  };

  const cmeColumns = [
    {
      title: "Mês",
      dataIndex: "month",
      key: "month",
      width: 100,
      fixed: "left",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Abertura",
      dataIndex: "open",
      key: "open",
      width: 100,
      align: "center",
    },
    {
      title: "Máxima",
      dataIndex: "high",
      key: "high",
      width: 100,
      align: "center",
    },
    {
      title: "Mínima",
      dataIndex: "low",
      key: "low",
      width: 100,
      align: "center",
    },
    {
      title: "Último",
      dataIndex: "last",
      key: "last",
      width: 100,
      align: "center",
    },
    {
      title: "Variação",
      dataIndex: "change",
      key: "change",
      width: 100,
      align: "center",
      render: (text) => {
        if (!text || text === "-") return text;
        const isPositive = text.startsWith("+");
        const isNegative = text.startsWith("-");
        return (
          <Tag color={isPositive ? "green" : isNegative ? "red" : "default"}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Ajuste",
      dataIndex: "settle",
      key: "settle",
      width: 100,
      align: "center",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Volume",
      dataIndex: "volume",
      key: "volume",
      width: 100,
      align: "center",
    },
    {
      title: "Contratos em Aberto",
      dataIndex: "openInterest",
      key: "openInterest",
      width: 150,
      align: "center",
    },
  ];

  useEffect(() => {
    setIsLoading(true);
    getData("petroleo_ny", "getLastPetroleoNy", "insertPetroleoNy")
      .then((data) => {
        console.log(data);
        setCotacoesData(data);
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setHasError(err?.message ?? "Ocorreu um erro.");
        console.log(err);
      });

    fetchCmeSettlements(cmeTradeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Seção de Edição de Cotação */}
      <DescriptionPeriodValue
        title={"Petróleo - NYMEX"}
        onClickButton={() => saveData(cotacoesData)}
        date={formatDateHourToBR(cotacoesData.data)}
        state={cotacoesData}
        setState={setCotacoesData}
        stateKey={"fechamento"}
        isLoading={isLoading}
        hasError={hasError}
      />

      {/* Tabela de Cotações CME Group */}
      <Card
        title={
          <Space align="center" wrap>
            <Title level={4} style={{ margin: 0 }}>
              {cmeHeader || "WTI Crude Oil Future"} - CME Group
            </Title>
            {cmeReportType && (
              <Tag color={cmeReportType === "Final" ? "green" : "orange"}>
                {cmeReportType}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <DatePicker
              value={cmeTradeDate}
              onChange={(date) => {
                if (date) setCmeTradeDate(date);
              }}
              format="DD/MM/YYYY"
              placeholder="Selecionar data"
              allowClear={false}
            />
            <Button
              type="primary"
              icon={<FaSync />}
              loading={cmeLoading}
              onClick={() => fetchCmeSettlements(cmeTradeDate)}
            >
              Buscar
            </Button>
          </Space>
        }
      >
        {cmeUpdateTime && (
          <Text type="secondary" style={{ display: "block", marginBottom: 10 }}>
            Última atualização: {cmeUpdateTime}
          </Text>
        )}
        <Table
          columns={cmeColumns}
          dataSource={cmeData}
          loading={cmeLoading}
          pagination={{
            pageSize: 12,
            showSizeChanger: true,
            pageSizeOptions: ["12", "24", "50", "100"],
            showTotal: (total) => `Total: ${total} registros`,
          }}
          size="small"
          bordered
          scroll={{ x: 950 }}
        />
      </Card>
    </div>
  );
}
